import { FaCheckCircle } from 'react-icons/fa';
import Button from '@/components/UI/Button';
import Card from '@/components/UI/Card';
import Disclosure from '@/components/UI/Disclosure';
import { formatExitCandidate } from './backtestStatusFormatters';
import type { StrategyQueueStatus } from './backtestStatusTypes';
import PopulationPriorityInput from './PopulationPriorityInput';

interface QueueActivityPanelProps {
    queueStatus: StrategyQueueStatus | undefined;
    populationPriority: number;
    isRaisingPriority: boolean;
    onPopulationPriorityChange: (priority: number) => void;
    onRaisePriority: () => void;
}

const reprioritizableStatuses = new Set<string>(['pending', 'pending_preparation', 'preparing', 'prepared']);

const QueueActivityPanel = ({
    queueStatus,
    populationPriority,
    isRaisingPriority,
    onPopulationPriorityChange,
    onRaisePriority
}: QueueActivityPanelProps) => {
    const byStatus = queueStatus?.byStatus ?? [];
    const maxCount = byStatus.reduce((max, row) => Math.max(max, row.count), 0);
    const reprioritizableCount = byStatus.filter((row) => reprioritizableStatuses.has(row.status)).reduce((sum, row) => sum + row.count, 0);

    return (
        <Card>
            <div className="p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                    <h3 className="text-lg font-semibold text-zinc-100">Queue activity</h3>
                    {queueStatus && (
                        <span className="rounded bg-zinc-800 px-2 py-0.5 text-xs text-zinc-400">{queueStatus.environment}</span>
                    )}
                </div>

                {!queueStatus || byStatus.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-8 text-sm text-zinc-500">
                        <FaCheckCircle aria-hidden="true" className="text-xl" />
                        No active jobs for this strategy.
                    </div>
                ) : (
                    <>
                        <div className="space-y-2">
                            {byStatus.map((row) => (
                                <div key={row.status}>
                                    <div className="mb-0.5 flex items-center justify-between text-sm text-zinc-200">
                                        <span className="capitalize">{row.status.toLowerCase()}</span>
                                        <span className="font-mono">{row.count}</span>
                                    </div>
                                    <div className="h-1.5 overflow-hidden rounded-full bg-zinc-800">
                                        <div
                                            className="h-full bg-cyan-500"
                                            style={{ width: `${maxCount > 0 ? Math.round((row.count / maxCount) * 100) : 0}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {((queueStatus.bySubmittedBy?.length ?? 0) > 0 || (queueStatus.byExitCandidate?.length ?? 0) > 0) && (
                            <Disclosure className="mt-3" title="Breakdown" variant="plain">
                                {queueStatus.bySubmittedBy && queueStatus.bySubmittedBy.length > 0 && (
                                    <div className="mb-2 space-y-1">
                                        <div className="text-xs text-zinc-500">Submitted by</div>
                                        {queueStatus.bySubmittedBy.map((row) => (
                                            <div className="flex justify-between text-xs text-zinc-300" key={row.submittedBy ?? '(null)'}>
                                                <span className="font-mono">{row.submittedBy ?? '(null)'}</span>
                                                <span className="font-mono">{row.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {queueStatus.byExitCandidate && queueStatus.byExitCandidate.length > 0 && (
                                    <div className="space-y-1">
                                        <div className="text-xs text-zinc-500">Exit candidates</div>
                                        {queueStatus.byExitCandidate.map((row) => (
                                            <div
                                                className="flex justify-between gap-3 text-xs text-zinc-300"
                                                key={`${row.exitCandidateId ?? 'null'}:${row.exitCandidateHash ?? 'null'}`}
                                            >
                                                <span className="font-mono">
                                                    {formatExitCandidate({
                                                        candidateId: row.exitCandidateId,
                                                        candidateHash: row.exitCandidateHash
                                                    })}
                                                </span>
                                                <span className="font-mono">{row.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Disclosure>
                        )}

                        <div className="mt-4 border-t border-zinc-700 pt-4">
                            <PopulationPriorityInput
                                id="activePopulationPriority"
                                priority={populationPriority}
                                onChange={onPopulationPriorityChange}
                            />
                            <Button
                                className="mt-3"
                                disabled={reprioritizableCount === 0}
                                isLoading={isRaisingPriority}
                                size="sm"
                                variant="secondary"
                                onClick={onRaisePriority}
                            >
                                Raise eligible active jobs
                            </Button>
                            <p className="mt-2 text-xs text-zinc-500">
                                Raises {reprioritizableCount} queued or preparing population job
                                {reprioritizableCount === 1 ? '' : 's'} below this value. Assigned and running jobs are unchanged.
                            </p>
                        </div>
                    </>
                )}
            </div>
        </Card>
    );
};

export default QueueActivityPanel;
